import type { UniqueImpoundResponseDto } from '@relay-towing/api';
import * as React from 'react';
import {
  Button,
  DetailField,
  Divider,
  EmptyState,
  impoundStatus,
  Loading,
  MobileHeader,
  MobileScreen,
  Stack,
  Text,
  sentenceCase,
} from '@relay-towing/ui';
import { Route } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useDriverImpound } from '../../hooks/useDriverImpound';
import { useMember } from '../../hooks/useMember';
import { CallNotes } from '../calls/CallNotes';
import { CallPhotos } from '../calls/CallPhotos';
import { CallVehicle } from '../calls/CallVehicle';
import { PlaceSheet } from '../calls/PlaceSheet';
import { useBack } from '../shell/useBack';
import { dayAndTime } from '../status';
import { vehicleOf } from '../vehicle';

const money = (amount: number | null | undefined) =>
  typeof amount === 'number'
    ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : undefined;

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString() : undefined;

/** The call's fields an office member is sent with its impound. */
interface ImpoundCall {
  pickupLocation?: string | null;
  pickupLocationLat?: number | null;
  pickupLocationLong?: number | null;
  startedAt?: string | null;
  completionTime?: string | null;
}

/**
 * One impound, laid out as a call is (Figma 915-1575): the vehicle and its
 * lot, Details with the pickup, its times and the photos, then Vehicle and
 * Notes, and the status in the header. A
 * member who only drives is sent the vehicle, its photos, the lot and the
 * notes, nothing more, so every other row is drawn only when its field
 * came: the screen reads what the server decided, not the role. An
 * impound's notes and photos are its vehicle's, the same ones the office
 * sees on the call, and its vehicle is the call's Vehicle section, the same
 * add, card and sheet. Anyone who may edit impounds may add evidence to one
 * they can see (photos, notes, the vehicle's details); the fees, holds, lot
 * and status are the office's, and are changed on the dashboard. The server
 * enforces both.
 */
export function ImpoundDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBack('/impounds');
  const member = useMember();
  const query = useDriverImpound(id);
  const impound = query.data as unknown as UniqueImpoundResponseDto | undefined;
  const [placing, setPlacing] = React.useState(false);

  const status = impound?.status ? impoundStatus(impound.status) : undefined;
  // The status in the middle as a call's is, when the server sent one; the
  // impound's number where there is none to show, for a driver-only member.
  const header = (
    <MobileHeader
      onBack={back}
      backLabel="Back to impounds"
      status={status ? { label: status.label, tone: status.tone } : undefined}
      title={status ? undefined : (impound?.impoundNumber ?? 'Impound')}
    />
  );

  if (query.isLoading)
    return <Loading label="Loading the impound" fill="screen" />;
  if (!impound)
    return (
      <MobileScreen header={header}>
        <EmptyState
          title="This impound is not available"
          description="It may have been deleted, or it is not one your role can see."
        />
      </MobileScreen>
    );

  // The server sends only the impounds the caller may see, and lets anyone
  // with impounds.edit add evidence to those.
  const addsEvidence = member.can('impounds.edit');
  // The call it came from, all of it to the office; a member who only
  // drives is sent its vehicle alone.
  const call = impound.dispatch as
    | (NonNullable<UniqueImpoundResponseDto['dispatch']> & ImpoundCall)
    | undefined;
  const vehicle = call?.vehicle;
  // The vehicle's photos, notes and details are kept by its id, which every
  // member who may see the impound is sent.
  const vehicleId = vehicle?.id;
  const lot = impound.location?.name || impound.storageLot;
  const plate = vehicle?.licensePlate;

  return (
    <MobileScreen header={header}>
      {/* The call's sections, as the Figma's impound (915-1575) draws them:
          the vehicle and its lot over Details, Vehicle and Notes, each ruled
          off from the next. */}
      <Stack gap={6}>
        <Stack gap={1}>
          <Text as="h1" variant="h1">
            {vehicleOf(vehicle) || impound.impoundNumber}
          </Text>
          {lot ? <Text tone="muted">{lot}</Text> : null}
          <Text tone="muted">
            {[impound.impoundNumber, plate].filter(Boolean).join(' · ')}
          </Text>
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Details
          </Text>
          {call?.pickupLocation ? (
            <>
              <DetailField
                label="Pickup Location"
                value={call.pickupLocation}
                action={
                  <Button
                    variant="secondary"
                    size="icon-lg"
                    pill
                    aria-label="Open in maps"
                    onClick={() => setPlacing(true)}
                  >
                    <Route />
                  </Button>
                }
              />
              <PlaceSheet
                title="Pickup location"
                place={{
                  address: call.pickupLocation,
                  lat: call.pickupLocationLat,
                  lng: call.pickupLocationLong,
                }}
                open={placing}
                onOpenChange={setPlacing}
              />
            </>
          ) : null}
          {call?.startedAt ? (
            <DetailField
              label="Dispatched"
              value={dayAndTime(call.startedAt)}
            />
          ) : null}
          {call?.completionTime ? (
            <DetailField
              label="Finished"
              value={dayAndTime(call.completionTime)}
            />
          ) : null}
          {/* Every other row only when its field came: the screen reads
              what the server decided, not the role. */}
          {impound.impoundDate ? (
            <DetailField label="Impounded" value={day(impound.impoundDate)} />
          ) : null}
          {typeof impound.daysHeld === 'number' ? (
            <DetailField label="Days held" value={impound.daysHeld} />
          ) : null}
          {impound.releasedDate ? (
            <DetailField label="Released" value={day(impound.releasedDate)} />
          ) : null}
          {impound.impoundType ? (
            <DetailField
              label="Type"
              value={sentenceCase(impound.impoundType)}
            />
          ) : null}
          {impound.driver?.name ? (
            <DetailField label="Towed by" value={impound.driver.name} />
          ) : null}
          {money(impound.total) ? (
            <DetailField label="Total" value={money(impound.total)} />
          ) : null}
          {money(impound.balanceDue) ? (
            <DetailField
              label="Balance due"
              value={money(impound.balanceDue)}
            />
          ) : null}
          {vehicleId ? (
            <CallPhotos vehicleId={vehicleId} editable={addsEvidence} />
          ) : null}
        </Stack>
        {vehicle ? (
          <>
            <Divider />
            <Stack gap={4}>
              <Text as="h2" variant="h2">
                Vehicle
              </Text>
              {/* The call's own Vehicle section: Add vehicle while it is
                  blank, then its card, which opens to change or clear it. */}
              <CallVehicle vehicle={vehicle} editable={addsEvidence} />
            </Stack>
          </>
        ) : null}
        {vehicleId ? (
          <>
            <Divider />
            <CallNotes vehicleId={vehicleId} editable={addsEvidence} />
          </>
        ) : null}
      </Stack>
    </MobileScreen>
  );
}
